import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import Spinner from 'icons/spinner';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import Link from 'next/link';
import ReCaptchaUI from 'components/ui/recaptcha';
import { PasswordInput } from 'components/ui/password-input';
import { AccountContext } from 'context/account';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginFormSchema } from './schema';
import { cn } from 'helpers/utils/classnames';
import FormEventsAnalyticsTracker from 'components/analaytics/form-event-anayltics-tracker';
import useFormInteraction from 'helpers/hooks/useFormInteraction';

interface Props {
  isPopupMode?: boolean;
  isCheckoutForm?: boolean;
  redirectLink?: string;
}
const LoginForm = ({ isPopupMode, isCheckoutForm, redirectLink = '/account' }: Props) => {
  const { login } = useContext(AccountContext);
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({ mode: 'onBlur', resolver: yupResolver(loginFormSchema) });
  const { push } = useRouter();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { handleStartInteraction, handleSubmitInteraction } = useFormInteraction('Login');
  const [isReCaptchaVerified, setIsReCaptchaVerified] = useState(isPopupMode || false);
  const [reCaptchaError, setReCaptchaError] = useState(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  useEffect(() => {
    if (isSuccess) {
      push(redirectLink);
    }
  }, [isSuccess, redirectLink]);

  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    if (isDirty && isMounted) {
      handleStartInteraction();
    }
  }, [isDirty, isMounted]);

  if (!isMounted) return null;
  return (
    <div className={cn('relative sm:w-[373px]', { 'sm:w-full': isPopupMode })}>
      <div
        className={cn('!w-full border-b border-primary-gold pb-8 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-[50px]', {
          'border-none lg:pr-0': isPopupMode,
          'border-b-0 border-t-[1px] pt-8 lg:border-l lg:border-r-0 lg:border-t-0 lg:pl-[50px] lg:pr-0 lg:pt-0':
            isCheckoutForm,
        })}
      >
        <h2
          className={cn('mb- text-center text-[28px] font-semibold leading-[34px] text-[#1B3144] lg:mb-14', {
            'text-2xl lg:mb-4': isPopupMode,
          })}
        >
          {isPopupMode ? 'Log in to Your Account' : 'Log In'}
        </h2>

        <form
          onSubmit={handleSubmit(async (data) => {
            if (isReCaptchaVerified) {
              try {
                const response = await login(data.email, data.password);
                if (response?.accountId) {
                  handleSubmitInteraction({ email: data.email });
                  setIsSuccess(true);
                } else {
                  toast.error(
                    'Sorry, the email and password combination that you have entered cannot be found. Please try again or select the “Forgot your password?”',
                  );
                }
              } catch (error) {
                toast.error('Something went wrong! Try again.');
              }
            } else {
              setReCaptchaError(true);
            }
          })}
          className="flex w-full flex-col gap-y-6"
        >
          {errors && <FormEventsAnalyticsTracker formName="Login" errors={errors} />}
          <div className="flex flex-col gap-y-3">
            <label className="text-sm font-semibold leading-[17px] text-primary-gray" htmlFor="loginEmail">
              Email
            </label>
            <Input
              aria-label="Login Email"
              className="w-full"
              type="email"
              autoComplete="email"
              required
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm font-normal leading-[17px] text-[#DA0F0F]">{errors.email.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-y-3">
            <label className="text-sm font-semibold leading-[17px] text-primary-gray" htmlFor="loginPassword">
              Password
            </label>
            <PasswordInput
              required
              aria-label="Login Password"
              type="password"
              {...register('password')}
              autoComplete="current-password"
              className="w-full"
            />
            {errors.password && (
              <p className="text-sm font-normal leading-[17px] text-[#DA0F0F]">{errors.password.message}</p>
            )}
          </div>

          {!isPopupMode && (
            <>
              <ReCaptchaUI setIsReCaptchaVerified={setIsReCaptchaVerified} setReCaptchaError={setReCaptchaError} />
              {reCaptchaError && (
                <p className="mt-1 text-xs font-semibold text-[#DA0F0F]">Please complete the reCaptcha</p>
              )}
            </>
          )}

          <div
            className={cn('mt-5 flex flex-col items-center gap-4 md:flex-row', {
              'mt-0': isPopupMode,
            })}
          >
            <Button
              aria-label="Log In"
              type="submit"
              className="flex h-[44px] w-full items-center justify-center rounded-[6px] bg-primary-lightGray px-6 text-sm font-bold leading-[17px] text-white focus:outline-offset-2 md:w-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner /> : 'Log In'}
            </Button>
            <Link
              href={'/forgot-password'}
              className="text-sm font-normal leading-[17px] text-primary-gray hover:underline"
            >
              Forgot your password?
            </Link>
          </div>
        </form>
      </div>
      {isPopupMode && (
        <>
          <div className="my-6 flex h-[1px] w-full bg-primary-gold" />
          <Link
            href={'/login'}
            className="mx-auto block text-center text-sm font-semibold leading-[17px] hover:underline"
          >
            Create an Account
          </Link>
        </>
      )}
    </div>
  );
};

export default LoginForm;
